import React from "react";
import {
  FormControl,
  TextField,
  Button,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormLabel,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import DebugPanel from "./DebugPanel";
import { useMsal } from "@azure/msal-react";

const validationSchema = Yup.object({
  purpose: Yup.string().required(
    "Pole wymagane. Wybierz cel zgłoszenia sprawy!"
  ),
  regionalOffice: Yup.string().required("Pole wymagane. Wybierz chorągiew!"),
  description: Yup.string().required("Pole wymagane. Podaj opis sytuacji!"),
  ageVictim: Yup.number()
    .min(0, "Wiek musi być większy lub równy 0!")
    .max(100, "Wiek musi być mniejszy lub równy 100!"),
  ageGuilty: Yup.number()
    .min(0, "Wiek musi być większy lub równy 0!")
    .max(100, "Wiek musi być mniejszy lub równy 100!"),
  age: Yup.number()
    .min(0, "Wiek musi być większy lub równy 0!")
    .max(100, "Wiek musi być mniejszy lub równy 100!"),
});

export default function Questions({
  setError,
  setSuccess,
  formType,
  affectType,
}) {
  const { accounts } = useMsal();
  const user = accounts[0];

  const mobbingAreaOptions = [
    "stałe ograniczanie lub utrudnianie możliwości wypowiadania się, stałe przerywanie wypowiedzi",
    "agresja słowna, również za pomocą zdalnych form komunikacji, tj. krzyk, obrażanie, zastraszanie, stosowanie obelg, poniżanie, grożenie przemocą fizyczną",
    "agresja wyrażona w sposób niewerbalny, np. poniżające i prowokujące gesty, ignorowanie, bezosobowe aluzje,",
    "odbieranie lub kwestionowanie kompetencji decyzyjnych, nieuzasadnione odbieranie zadań, nadmierna kontrola i niekonstruktywna krytyka,",
    "zlecanie wykonywania zadań sprzecznych ze sobą, nieadekwatnych do posiadanych kompetencji, naruszających godność, niezlecanie żadnych zadań do wykonania, bezpodstawne odbieranie zleconych wcześniej zadań, przydzielanie zadań w nadmiernej ilości, zlecanie zadań niewykonalnych w określonym czasie,",
    "utrudnianie lub odbieranie dostępu do środków i materiałów potrzebnych do wykonywania zadań lub celowe umieszczanie w miejscu fizycznie oddalonym lub odosobnionym,",
    "nieadekwatne, niezgodne z rzeczywistością ocenianie zaangażowania w wykonywanie zadań, jakości pracy i kompetencji zawodowych,",
    "izolacja, nieuzasadnione unikanie kontaktu, wykluczenie z grupy, brak lub blokowanie możliwości rozmów, ukrywanie istotnych informacji, dążenie do wyeliminowania z zespołu.",
  ];

  const handleMobbingAreaChange = (areaIndex, level) => {
    const currentMobbingAreas = { ...formik.values.mobbingAreas };
    if (level === "" || level === undefined) {
      delete currentMobbingAreas[areaIndex];
    } else {
      currentMobbingAreas[areaIndex] = parseInt(level);
    }
    formik.setFieldValue("mobbingAreas", currentMobbingAreas);
  };

  const formik = useFormik({
    initialValues: {
      anonymous: formType,
      affectsYou: affectType,
      purpose: "",
      nameSurname: formType === 2 && affectType === 1 ? user.name : "",
      phoneNumber: "",
      emailAddress: formType === 2 ? user.username : "",
      role: "",
      age: "",
      nameSurnameVictim: formType === 2 && affectType === 0 ? user.name : "",
      phoneNumberVictim: "",
      emailAddressVictim: "",
      roleVictim: "",
      regionalOffice: "",
      districtOffice: "",
      nameSurnameGuilty: "",
      ageVictim: "",
      ageGuilty: "",
      infoGuilty: "",
      description: "",
      isCrime: "",
      policeInformed: "",
      isCamp: "",
      action: "",
      nameSurnameCampCommisioner: "",
      emailAddressCampCommisioner: "",
      phoneNumberCampCommisioner: "",
      isDistrictCommisionerGuilty: "",
      isRegionalOfficeCommisionerGuilty: "",
      mobbingAreas: {},
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        const response = await axios.post(
          process.env.REACT_APP_API_URL + "/api/AddIssue",
          values
        );
        if (response.status >= 200 && response.status < 300) {
          setSuccess({
            alert: "Dziękujemy za przesłanie tego zgłoszenia!",
            description:
              "Sprawa zostanie przekazana do odpowiedniego komendanta hufca, chorągwi lub na poziom centralny (w zależności od tego, która z opcji została wybrana w formularzu). Jeśli podałeś/aś sój numer telefonu może skontaktować się z Tobą Strefa Pomocy ZHP lub osoba prowadząca interwencję w środowisku działania, aby uzyskać więcej informacji. Dziękujemy za Twoje zaangażowanie w budowanie kultury bezpieczeństwa w ZHP! Twoje zgłoszenie otrzymało numer:",
            pre: response.data.key,
          });
          resetForm();
        } else {
          throw new Error("Nieoczekiwana odpowiedź serwera");
        }
      } catch (error) {
        setError(
          error.response?.data?.message ||
            "Wystąpił błąd podczas wysyłania zgłoszenia."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <>
      <DebugPanel title="Formik" data={formik} />
      <DebugPanel title="Sate" data={{ formType, affectType }} />

      <Box component="form" onSubmit={formik.handleSubmit}>
        {affectType === 1 && (
          <Box sx={{ mt: 2 }}>
            <FormControl>
              <FormLabel id="isCamp">
                Czy zgłaszasz tę sprawę za zgodą osoby doświadczającej mobbingu?
              </FormLabel>
              <RadioGroup
                aria-labelledby="isCamp"
                name="isCamp"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.isCamp}
                error={formik.errors.isCamp}
              >
                <FormControlLabel
                  control={<Radio />}
                  label="Tak"
                  name="isCamp"
                  value="Tak"
                  sx={{
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      marginBottom: 0,
                    },
                  }}
                />
                <FormControlLabel
                  value="Nie"
                  control={<Radio />}
                  label="Nie"
                  name="isCamp"
                  sx={{
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      marginBottom: 0,
                    },
                  }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {affectType === 1 && formType !== 0 && (
          <>
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Osoba zgłaszająca
              </Typography>
            </Box>

            <TextField
              fullWidth
              variant="filled"
              label="Twoje imię i nazwisko"
              name="nameSurname"
              margin="normal"
              value={formik.values.nameSurname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.nameSurname && Boolean(formik.errors.nameSurname)
              }
              helperText={
                formik.touched.nameSurname && formik.errors.nameSurname
                  ? formik.errors.nameSurname
                  : "Pamiętaj, że podanie nam swoich danych jest dobrowolne!"
              }
              disabled={formType === 2}
            />

            <TextField
              fullWidth
              type="number"
              variant="filled"
              label="Twój numer telefonu"
              name="phoneNumber"
              margin="normal"
              value={formik.values.phoneNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
              }
              helperText={
                formik.touched.phoneNumber && formik.errors.phoneNumber
                  ? formik.errors.phoneNumber
                  : "Twój telefon zostanie wykorzystany do nawiązania kontaktu z Tobą w celu weryfikacji tego, jak czujesz się po dokonaniu zgłoszenia i czy nie potrzebujesz dodatkowej pomocy oraz zostaną przekazane osobie prowadzącej interwencję w tej sprawie (jeśli nie jesteś nią Ty)."
              }
            />

            <TextField
              fullWidth
              type="email"
              variant="filled"
              label="Twój adres e-mail"
              name="emailAddress"
              margin="normal"
              value={formik.values.emailAddress}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.emailAddress &&
                Boolean(formik.errors.emailAddress)
              }
              helperText={
                formik.touched.emailAddress && formik.errors.emailAddress
                  ? formik.errors.emailAddress
                  : "Twój adres e-mail zostanie wykorzystany do kontaktu z Tobą."
              }
              disabled={formType === 2}
            />

            <TextField
              fullWidth
              variant="filled"
              label="Twoja funkcja"
              name="role"
              margin="normal"
              value={formik.values.role}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.role && Boolean(formik.errors.role)}
              helperText={
                formik.touched.role && formik.errors.role
                  ? formik.errors.role
                  : "Jaką funkcję pełnisz w ZHP? Jeśli nie pełnisz żadnej, przejdź dalej."
              }
            />
          </>
        )}

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            {affectType === 0
              ? "Ty (osoba doświadczająca mobbingu"
              : "Osoba doświadczająca mobbingu"}
          </Typography>
        </Box>
        {((affectType === 0 && formType !== 0) ||
          (affectType === 1 && formType === 0) ||
          (affectType === 1 && formType !== 0)) && (
          <>
            <TextField
              fullWidth
              variant="filled"
              label={
                affectType === 0
                  ? "Twoje imię i nazwisko"
                  : "Imię i nazwisko osoby doświadczającej mobbingu"
              }
              name="nameSurnameVictim"
              margin="normal"
              value={formik.values.nameSurnameVictim}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.nameSurnameVictim &&
                Boolean(formik.errors.nameSurnameVictim)
              }
              helperText={
                formik.touched.nameSurnameVictim &&
                formik.errors.nameSurnameVictim
                  ? formik.errors.nameSurnameVictim
                  : null
              }
              disabled={formType === 2 && affectType === 0}
            />
            {affectType === 0 ? (
              <>
                <TextField
                  fullWidth
                  type="number"
                  variant="filled"
                  label="Twój numer telefonu"
                  name="phoneNumber"
                  margin="normal"
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.phoneNumber &&
                    Boolean(formik.errors.phoneNumber)
                  }
                  helperText={
                    formik.touched.phoneNumber && formik.errors.phoneNumber
                      ? formik.errors.phoneNumber
                      : "Twój telefon zostanie wykorzystany do nawiązania kontaktu z Tobą w celu weryfikacji tego, jak czujesz się po dokonaniu zgłoszenia i czy nie potrzebujesz dodatkowej pomocy oraz zostaną przekazane osobie prowadzącej interwencję w tej sprawie (jeśli nie jesteś nią Ty)."
                  }
                />

                <TextField
                  fullWidth
                  type="email"
                  variant="filled"
                  label="Twój adres e-mail"
                  name="emailAddress"
                  margin="normal"
                  value={formik.values.emailAddress}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.emailAddress &&
                    Boolean(formik.errors.emailAddress)
                  }
                  helperText={
                    formik.touched.emailAddress && formik.errors.emailAddress
                      ? formik.errors.emailAddress
                      : "Twój adres e-mail zostanie wykorzystany do kontaktu z Tobą."
                  }
                  disabled={formType === 2 && affectType === 0}
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  type="number"
                  variant="filled"
                  label="Numer telefonu osoby doświadczającej mobbingu"
                  name="phoneNumberVictim"
                  margin="normal"
                  value={formik.values.phoneNumberVictim}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.phoneNumberVictim &&
                    Boolean(formik.errors.phoneNumberVictim)
                  }
                  helperText={
                    formik.touched.phoneNumberVictim &&
                    formik.errors.phoneNumberVictim
                      ? formik.errors.phoneNumberVictim
                      : "Telefon zostanie wykorzystany do nawiązania kontaktu z tą osobą."
                  }
                />

                <TextField
                  fullWidth
                  type="email"
                  variant="filled"
                  label="Adres e-mail osoby doświadczającej mobbingu"
                  name="emailAddressVictim"
                  margin="normal"
                  value={formik.values.emailAddressVictim}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.emailAddressVictim &&
                    Boolean(formik.errors.emailAddressVictim)
                  }
                  helperText={
                    formik.touched.emailAddressVictim &&
                    formik.errors.emailAddressVictim
                      ? formik.errors.emailAddressVictim
                      : "Twój adres e-mail zostanie wykorzystany do kontaktu z tą osobą."
                  }
                  disabled={formType === 2 && affectType === 0}
                />
              </>
            )}
          </>
        )}

        <TextField
          fullWidth
          variant="filled"
          label={
            affectType === 0
              ? "Twoja funkcja"
              : "Funkcja osoby doświadczającej mobbingu"
          }
          name="roleVictim"
          margin="normal"
          value={formik.values.roleVictim}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.roleVictim && Boolean(formik.errors.roleVictim)}
          helperText={
            formik.touched.roleVictim && formik.errors.roleVictim
              ? formik.errors.roleVictim
              : affectType === 0
              ? "Jaką funkcję pełnisz w ZHP? Jeśli nie pełnisz żadnej, przejdź dalej."
              : "Jaką funkcję lub role pełni ta osoba? Czy jest szeregowym harcerzem, czy np. zastępowym lub drużynowym?"
          }
        />

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            Opis sytuacji
          </Typography>
          <Typography>
            Poprzez mobbing należy rozumieć działania lub zachowania skierowane
            wobec osoby objętej Polityką, polegające na uporczywym i
            długotrwałym nękaniu lub zastraszaniu tej osoby, wywołujące zaniżoną
            ocenę kompetencji, powodujące lub mające na celu poniżenie lub
            ośmieszenie osoby, izolowanie jej lub wyeliminowanie z zespołu.
          </Typography>
          <Typography>
            Zachowania, które mogą być uznane za mobbing, mogą występować
            samodzielnie lub łącznie i mogą to być w szczególności zachowania
            wymienione poniżej.
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <FormLabel>
              Obszary mobbingu (możesz zaznaczyć więcej niż jedną opcję i
              określić poziom wystąpienia od 0 do 3):
            </FormLabel>
            <Box sx={{ mt: 2 }}>
              {mobbingAreaOptions.map((area, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          formik.values.mobbingAreas[index] !== undefined
                        }
                        onChange={(e) => {
                          if (!e.target.checked) {
                            handleMobbingAreaChange(index, "");
                          } else {
                            handleMobbingAreaChange(index, 0);
                          }
                        }}
                      />
                    }
                    label={area}
                    sx={{
                      alignItems: "center",
                      "& .MuiFormControlLabel-label": {
                        marginBottom: 0,
                      },
                    }}
                  />
                  {formik.values.mobbingAreas[index] !== undefined && (
                    <Box sx={{ ml: 4, mt: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Określ poziom uciążliwości tego zachowania:
                      </Typography>
                      <RadioGroup
                        row
                        value={formik.values.mobbingAreas[index] || 0}
                        onChange={(e) =>
                          handleMobbingAreaChange(
                            index,
                            parseInt(e.target.value)
                          )
                        }
                      >
                        <FormControlLabel
                          value={0}
                          control={<Radio />}
                          label="Bardzo nisko"
                          sx={{
                            alignItems: "center",
                            "& .MuiFormControlLabel-label": {
                              marginBottom: 0,
                            },
                          }}
                        />
                        <FormControlLabel
                          value={1}
                          control={<Radio />}
                          label="Nisko"
                          sx={{
                            alignItems: "center",
                            "& .MuiFormControlLabel-label": {
                              marginBottom: 0,
                            },
                          }}
                        />
                        <FormControlLabel
                          value={2}
                          control={<Radio />}
                          label="Średnio"
                          sx={{
                            alignItems: "center",
                            "& .MuiFormControlLabel-label": {
                              marginBottom: 0,
                            },
                          }}
                        />
                        <FormControlLabel
                          value={3}
                          control={<Radio />}
                          label="Wysoko"
                          sx={{
                            alignItems: "center",
                            "& .MuiFormControlLabel-label": {
                              marginBottom: 0,
                            },
                          }}
                        />
                      </RadioGroup>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </FormControl>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControl>
            <FormLabel id="policeInformed">
              Jak często spotykasz się z wybranym zachowaniem?
            </FormLabel>
            <RadioGroup
              aria-labelledby="policeInformed"
              name="policeInformed"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.policeInformed}
              error={formik.errors.policeInformed}
            >
              <FormControlLabel
                control={<Radio />}
                label="Rzadziej niż raz w miesiącu"
                name="policeInformed"
                value="Rzadziej niż raz w miesiącu"
                sx={{
                  alignItems: "center",
                  "& .MuiFormControlLabel-label": {
                    marginBottom: 0,
                  },
                }}
              />
              <FormControlLabel
                value="Raz na miesiąc"
                control={<Radio />}
                label="Raz na miesiąc"
                name="policeInformed"
                sx={{
                  alignItems: "center",
                  "& .MuiFormControlLabel-label": {
                    marginBottom: 0,
                  },
                }}
              />
              <FormControlLabel
                value="Kilka razy w miesiącu"
                control={<Radio />}
                label="Kilka razy w miesiącu"
                name="policeInformed"
                sx={{
                  alignItems: "center",
                  "& .MuiFormControlLabel-label": {
                    marginBottom: 0,
                  },
                }}
              />
              <FormControlLabel
                value="Raz na tydzień"
                control={<Radio />}
                label="Raz na tydzień"
                name="policeInformed"
                sx={{
                  alignItems: "center",
                  "& .MuiFormControlLabel-label": {
                    marginBottom: 0,
                  },
                }}
              />
              <FormControlLabel
                value="Kilka razy w tygodniu"
                control={<Radio />}
                label="Kilka razy w tygodniu"
                name="policeInformed"
                sx={{
                  alignItems: "center",
                  "& .MuiFormControlLabel-label": {
                    marginBottom: 0,
                  },
                }}
              />
              <FormControlLabel
                value="Codziennie"
                control={<Radio />}
                label="Codziennie"
                name="policeInformed"
                sx={{
                  alignItems: "center",
                  "& .MuiFormControlLabel-label": {
                    marginBottom: 0,
                  },
                }}
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <TextField
          fullWidth
          variant="filled"
          label="Opisz jak wyglądały sytuacje, z jakimi miałaś/miałeś do czynienia?"
          name="description"
          multiline
          rows={4}
          margin="normal"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.description && Boolean(formik.errors.description)
          }
          helperText={
            formik.touched.description && formik.errors.description
              ? formik.errors.description
              : "Pole wymagane."
          }
        />
        <TextField
          fullWidth
          variant="filled"
          label="Kto był sprawcą sytuacji?"
          name="description2"
          multiline
          rows={4}
          margin="normal"
          value={formik.values.description2}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.description2 && Boolean(formik.errors.description2)
          }
          helperText={
            formik.touched.description2 && formik.errors.description2
              ? formik.errors.description2
              : "Pole wymagane."
          }
          placeholder="W jakiej zależności służbowej znajdowałaś/znajdowałeś się w momencie zdarzenia - w stosunku do sprawcy?"
        />
        <TextField
          fullWidth
          variant="filled"
          label="W jakiej zależności służbowej w stosunku do sprawcy znajdujesz się w chwili obecnej?"
          name="role"
          margin="normal"
          value={formik.values.role}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.role && Boolean(formik.errors.role)}
          helperText={formik.touched.role && formik.errors.role}
        />
        <TextField
          fullWidth
          variant="filled"
          label="Tutaj możesz pozostawić dodatkowe informacje związane z przetwarzaniem zgłoszenia."
          name="actions"
          multiline
          rows={4}
          margin="normal"
          value={formik.values.actions}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.actions && Boolean(formik.errors.actions)}
          helperText={formik.touched.actions && formik.errors.actions}
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={true}
          sx={{
            backgroundColor: "#850C70",
            "&:hover": {
              backgroundColor: "#5F0054",
            },
            mt: 2,
          }}
        >
          Wyślij
        </Button>
        <Button
          onClick={formik.handleReset}
          variant="outlined"
          sx={{
            ml: 2,
            mt: 2,
            borderColor: "#850C70",
            color: "#850C70",
            "&:hover": {
              backgroundColor: "#5F0054",
              color: "#fff",
              borderColor: "#5F0054",
            },
          }}
        >
          Anuluj
        </Button>
      </Box>
    </>
  );
}
